import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTRPCClientAPI } from '../../../../trpc/trpc';
import { randomState, sha256Hex } from './nonce';

// The server keeps a nonce for 10 minutes. We stop using it after 8 minutes.
export const APPLE_NONCE_MAX_AGE_MS = 8 * 60 * 1000;
// After a failed fetch, wait this long before the next try. We do not poll.
const RETRY_DELAY_MS = 30 * 1000;

export interface PreparedAppleNonce {
  // The raw nonce. The server gets this value. Keep it in memory only.
  rawNonce: string;
  // The hex SHA-256 of the raw nonce. Apple gets this value.
  hashedNonce: string;
  // The OAuth state we give to Apple. The popup must return the same value.
  state: string;
  fetchedAt: number;
}

export interface UseAppleNonceReturn {
  ready: boolean;
  // The last fetch failed. The page tells the person; the hook tries again after a delay.
  failed: boolean;
  // Returns the prepared nonce and marks it as used. Returns null when no fresh nonce is ready.
  // It is synchronous, so the Apple popup can still open inside the click handler.
  take: () => PreparedAppleNonce | null;
  // Fetches a new nonce. Call it after every Apple attempt.
  refresh: () => void;
}

// Prefetches a single-use nonce for Sign in with Apple (auth.socialNonce).
export const useAppleNonce = (enabled: boolean): UseAppleNonceReturn => {
  const trpcClient = useTRPCClientAPI();
  // A ref keeps `refresh` stable, so the mount effect runs once per `enabled` value.
  const trpcClientRef = useRef(trpcClient);
  trpcClientRef.current = trpcClient;
  const [prepared, setPrepared] = useState<PreparedAppleNonce | null>(null);
  const [failed, setFailed] = useState(false);
  const preparedRef = useRef<PreparedAppleNonce | null>(null);
  // Only the newest fetch may store its result.
  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  const store = useCallback((value: PreparedAppleNonce | null) => {
    preparedRef.current = value;
    if (mountedRef.current) {
      setPrepared(value);
    }
  }, []);

  const refresh = useCallback(() => {
    if (!enabled) {
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    clearTimeout(timerRef.current);
    store(null);

    const load = async (): Promise<void> => {
      try {
        const { nonce } = await trpcClientRef.current.auth.socialNonce.mutate();
        const hashedNonce = await sha256Hex(nonce);
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        store({ rawNonce: nonce, hashedNonce, state: randomState(), fetchedAt: Date.now() });
        setFailed(false);
        // Get a new nonce before this one becomes stale.
        timerRef.current = setTimeout(refresh, APPLE_NONCE_MAX_AGE_MS);
      } catch {
        // Do not log the error. It can hold request data.
        if (mountedRef.current && requestId === requestIdRef.current) {
          setFailed(true);
          timerRef.current = setTimeout(refresh, RETRY_DELAY_MS);
        }
      }
    };
    load().catch(console.error);
  }, [enabled, store]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [refresh]);

  const take = useCallback((): PreparedAppleNonce | null => {
    const { current } = preparedRef;
    if (!current) {
      return null;
    }
    if (Date.now() - current.fetchedAt > APPLE_NONCE_MAX_AGE_MS) {
      refresh();
      return null;
    }
    // A nonce is single-use. Clear it, so a second click cannot send it again.
    clearTimeout(timerRef.current);
    store(null);
    return current;
  }, [refresh, store]);

  const ready = prepared !== null;
  return useMemo(() => ({ ready, failed, take, refresh }), [ready, failed, take, refresh]);
};
